export type PillarId =
  | 'evidence-and-reality-testing'
  | 'emotional-regulation'
  | 'consequential-and-long-term-reasoning'
  | 'manipulation-resistance'
  | 'responsible-agency-and-civic-reciprocity'

export type ReviewStatus = 'Reviewed' | 'Awaiting review' | 'Insufficient evidence'

export type Finding = {
  task: string
  excerpt: string
  observedReasoning: string
  rubric: string
  limitations: string
  reviewStatus: ReviewStatus
  activity: string
  reflection: string
  history: { date: string; event: string }[]
}

export type Pillar = {
  id: PillarId
  order: number
  name: string
  shortLabel: string
  description: string
  guidingQuestion: string
  overview: string[]
  subdomains: string[]
  developmentActivities: string[]
  exampleFinding: Finding
}

export const pillarHref = (id: PillarId) => `/ncji/${id}`

export const pillars: Pillar[] = [
  {
    id: 'evidence-and-reality-testing',
    order: 1,
    name: 'Evidence and Reality Testing',
    shortLabel: 'Evidence & Reality',
    description:
      'Evaluate sources, distinguish observation from inference, calibrate confidence and revise conclusions when evidence changes.',
    guidingQuestion: 'What is supported, and what would change my mind?',
    overview: [
      'Evidence and reality testing is the ability to evaluate claims clearly, weigh evidence appropriately, recognize contradictions and process complexity without collapsing into confusion, impulse or oversimplification.',
      'It is not a measure of intelligence or education. A person may be knowledgeable, verbal or credentialed and still reason poorly under pressure, emotion, ideology or social influence. Strong reasoning involves asking whether a claim is supported, whether assumptions are being smuggled in, and whether conclusions actually follow, while recognizing when more information is needed.',
    ],
    subdomains: [
      'Source evaluation',
      'Observation versus inference',
      'Confidence calibration',
      'Contradiction detection',
      'Revising conclusions when evidence changes',
    ],
    developmentActivities: [
      'Claim-and-evidence analysis exercises',
      'Comparing sources on the same question',
      'Calibration practice: estimating confidence, then checking outcomes',
      'Argument mapping during long-form reading',
      'Structured debate, including stating the strongest opposing case',
    ],
    exampleFinding: {
      task: 'Practice task E-02 · Compare two reports on a local water-quality claim',
      excerpt:
        '“The second report is more convincing because it was shared more widely, but it does not say how samples were collected.”',
      observedReasoning:
        'Noticed missing sampling information, but initially treated how widely a report was shared as evidence of reliability.',
      rubric:
        'Partially meets the criterion for source evaluation: identifies a methodological gap; does not yet separate popularity from evidential support.',
      limitations: 'A single practice task on one topic. Does not indicate how the person reasons in other domains.',
      reviewStatus: 'Reviewed',
      activity: 'Source comparison exercise: rate three reports on method, not reach.',
      reflection: '“I hadn’t noticed I was using shares as a shortcut.”',
      history: [
        { date: 'Day 1', event: 'Task completed' },
        { date: 'Day 3', event: 'Reviewed by assessor; rubric note added' },
        { date: 'Day 4', event: 'Development activity assigned' },
      ],
    },
  },
  {
    id: 'emotional-regulation',
    order: 2,
    name: 'Emotional Regulation',
    shortLabel: 'Emotional Regulation',
    description: 'Maintain or recover constructive deliberation under frustration, disagreement and pressure.',
    guidingQuestion: 'Can I continue evaluating fairly when emotions are involved?',
    overview: [
      'Emotional regulation is the ability to remain stable enough under stress, frustration, pressure or conflict to think clearly and act responsibly. It does not mean suppressing emotion or becoming detached. It means experiencing emotion without being ruled by it, and recovering from intensity without letting it distort judgment, relationships or performance.',
      'Reasoning, planning, cooperation and leadership all deteriorate when people become excessively reactive or destabilized. Weak regulation can lead to rash decisions, conflict escalation and fear-driven thinking; strong regulation supports steadier decisions and greater reliability in high-stress environments.',
    ],
    subdomains: [
      'Stress tolerance',
      'Recovery after setbacks',
      'De-escalation in disagreement',
      'Impulse control',
      'Maintaining perspective under pressure',
    ],
    developmentActivities: [
      'Pause-and-reframe exercises during simulated disagreements',
      'Reflection after difficult decisions',
      'Conflict-resolution practice',
      'Stress-management, sleep and recovery routines',
      'Behavioral coaching on triggers and patterns',
    ],
    exampleFinding: {
      task: 'Practice task R-01 · Respond to a heated message in a team discussion',
      excerpt: '“I want to answer this now, but I’ll draft it and come back in ten minutes.”',
      observedReasoning:
        'Recognized the urge to respond immediately and chose a delay before replying; the final reply addressed the substance of the disagreement.',
      rubric:
        'Meets the criterion for recovering constructive deliberation: names the reaction, delays action and returns to the issue.',
      limitations: 'A simulated, low-stakes scenario. Real pressure and fatigue may produce different responses.',
      reviewStatus: 'Awaiting review',
      activity: 'Practise the same approach in a timed scenario with competing deadlines.',
      reflection: '“Writing the draft first helped more than I expected.”',
      history: [
        { date: 'Day 1', event: 'Task completed' },
        { date: 'Day 2', event: 'Queued for human review' },
      ],
    },
  },
  {
    id: 'consequential-and-long-term-reasoning',
    order: 3,
    name: 'Consequential and Long-Term Reasoning',
    shortLabel: 'Consequences & Time',
    description: 'Compare options, trade-offs and likely consequences across people, systems and time.',
    guidingQuestion: 'What is likely to follow, for whom, and over what horizon?',
    overview: [
      'Consequential and long-term reasoning is the ability to understand that actions produce downstream effects, often beyond the immediate moment. It includes thinking through trade-offs, anticipating second-order effects, recognizing how one decision reshapes later choices, and judging whether a short-term gain may create a larger long-term cost.',
      'Many damaging mistakes in life, institutions and government come not from ignorance but from narrow thinking that focuses on the immediate, the visible or the emotionally satisfying. Long-term reasoning is explicit within this pillar: the question is not only what follows next, but what follows over months and years.',
    ],
    subdomains: [
      'Trade-off analysis',
      'Second-order effects',
      'Short-term versus long-term horizons',
      'Effects on other people and systems',
      'Decisions under uncertainty',
    ],
    developmentActivities: [
      'Decision simulations with delayed consequences',
      'Scenario planning across different time horizons',
      'Pre-mortems before committing to a plan',
      'Consequence mapping for affected groups',
      'Milestone planning and review',
    ],
    exampleFinding: {
      task: 'Practice task C-03 · Choose between two budget options for a community program',
      excerpt: '“Option A saves money this year, but we would lose the trained volunteers by next spring.”',
      observedReasoning:
        'Compared immediate savings with a later capacity loss and identified who would be affected by each option.',
      rubric:
        'Meets the criterion for trade-off analysis across time; partially addresses effects on groups outside the program.',
      limitations: 'Based on a written scenario with fixed information. Does not show how the person gathers missing information.',
      reviewStatus: 'Reviewed',
      activity: 'Consequence mapping exercise that adds a third affected group.',
      reflection: '“I focused on the volunteers and forgot the families using the service.”',
      history: [
        { date: 'Day 1', event: 'Task completed' },
        { date: 'Day 2', event: 'Reviewed by assessor' },
        { date: 'Day 9', event: 'Development activity completed' },
      ],
    },
  },
  {
    id: 'manipulation-resistance',
    order: 4,
    name: 'Manipulation Resistance',
    shortLabel: 'Manipulation Resistance',
    description:
      'Recognize misleading framing, selective evidence and social pressure while remaining receptive to well-supported information.',
    guidingQuestion: 'Is this influence justified by the evidence?',
    overview: [
      'Manipulation resistance is the ability to detect and resist propaganda, emotional framing, rhetorical deception, identity-based pressure and narrative capture. It is not about distrusting everything. It is about noticing when someone is trying to move judgment through distortion, fear, tribal loyalty or selective framing rather than through sound reasoning and evidence.',
      'Modern information systems are often optimized for attention and reaction rather than clarity. Without manipulation resistance, people and institutions become vulnerable to narratives that feel compelling while degrading judgment. Equally, resistance must not become reflexive dismissal: well-supported information should still change minds.',
    ],
    subdomains: [
      'Recognizing misleading framing',
      'Detecting selective evidence',
      'Identity and social pressure',
      'Emotional appeals',
      'Openness to well-supported information',
    ],
    developmentActivities: [
      'Media literacy exercises',
      'Rhetoric and propaganda analysis',
      'Comparing coverage of one event across sources',
      'Historical case studies of mass persuasion',
      'Awareness of personal emotional and identity triggers',
    ],
    exampleFinding: {
      task: 'Practice task M-01 · Evaluate a short advertisement for a health supplement',
      excerpt: '“The testimonials are moving, but there’s no comparison group. I’d want to see a trial.”',
      observedReasoning:
        'Identified emotional testimony used in place of evidence and named the kind of evidence that would be persuasive.',
      rubric:
        'Meets the criterion for detecting selective evidence while remaining open to well-supported information.',
      limitations: 'One commercial example. Political or identity-related content may produce different responses.',
      reviewStatus: 'Reviewed',
      activity: 'Compare framing of the same news event across three outlets.',
      reflection: '“Easier to spot when I don’t care about the product.”',
      history: [
        { date: 'Day 1', event: 'Task completed' },
        { date: 'Day 4', event: 'Reviewed by assessor' },
      ],
    },
  },
  {
    id: 'responsible-agency-and-civic-reciprocity',
    order: 5,
    name: 'Responsible Agency and Civic Reciprocity',
    shortLabel: 'Agency & Reciprocity',
    description:
      'Act with accountability, consider affected people, apply standards consistently and respect reciprocal responsibilities.',
    guidingQuestion: 'How should I act toward others, and what must I answer for?',
    overview: [
      'Responsible agency is the capacity to turn judgment into disciplined action: organizing effort, following through, cooperating with others and accepting accountability for results. Civic reciprocity is explicit within this pillar: applying the same standards to oneself as to others, and respecting the responsibilities people owe one another within shared institutions.',
      'This pillar brings together self-direction and execution, social cooperation and leadership responsibility. As responsibility grows, so does the cost of acting without regard for affected people. Strong responsible agency supports trust, continuity and healthier institutions; weak agency can appear as blame-shifting, inconsistent standards, withdrawal from shared responsibility or failure to follow through.',
    ],
    subdomains: [
      'Accountability for decisions and results',
      'Consideration of affected people',
      'Consistent standards for self and others',
      'Reciprocal responsibilities',
      'Follow-through and cooperation',
    ],
    developmentActivities: [
      'Commitment and follow-through plans with milestones',
      'Team and civic projects with shared responsibilities',
      'Accountability reviews after completed work',
      'Mentoring and peer feedback',
      'Leadership scenario exercises',
    ],
    exampleFinding: {
      task: 'Practice task A-02 · Respond to a missed deadline that affected a partner team',
      excerpt: '“The delay was mostly caused by the supplier, but I should have warned the other team earlier.”',
      observedReasoning:
        'Distinguished external causes from their own responsibility and identified a concrete action owed to the affected team.',
      rubric:
        'Meets the criterion for accountability; partially meets the criterion for applying the same standard used when judging the supplier.',
      limitations: 'A written reflection on a hypothetical case. Does not show actual follow-through.',
      reviewStatus: 'Awaiting review',
      activity: 'Draft and complete a follow-through plan with two checkpoints.',
      reflection: '“I was quicker to blame the supplier than to own my part.”',
      history: [
        { date: 'Day 1', event: 'Task completed' },
        { date: 'Day 2', event: 'Queued for human review' },
      ],
    },
  },
]

export const getPillar = (id: PillarId) => pillars.find((pillar) => pillar.id === id) as Pillar

/** Shown wherever NCJI's five pillars are presented. */
export const pillarResearchStatus =
  'The five-pillar architecture is established as our working model. The assessment instruments remain under development. Pilot studies are needed to establish reliability, validity, lasting improvement and transfer to real-world decisions.'

export const ncjiPillarsIntro =
  'The Neurocognitive Civic Judgment Index is designed to examine how people evaluate evidence, regulate their responses, reason through consequences, resist manipulation and act responsibly toward others. Its five-pillar architecture connects task-specific observations with feedback, review and development.'

/** The general HCF cycle. Describes how development moves, not what is assessed. */
export const hcfCycle = [
  { step: 'Assess', description: 'Structured exercises with transparent criteria' },
  { step: 'Review', description: 'Human review of findings and their limits' },
  { step: 'Develop', description: 'Purposeful practice and support' },
  { step: 'Reassess', description: 'Track change over time' },
]

/** The detailed NCJI pathway within HCF. */
export const ncjiPathway = [
  { step: 'Assess', description: 'Assess demonstrated judgment' },
  { step: 'Explain', description: 'Explain findings' },
  { step: 'Recommend', description: 'Recommend development activities' },
  { step: 'Support', description: 'Provide support' },
  { step: 'Reassess', description: 'Reassess progress' },
  { step: 'Advance', description: 'Move toward the next goal' },
]

export const ncjiPathwayExplanation =
  'HCF provides the overall assessment and development cycle. NCJI expresses that cycle as a more detailed pathway: assess demonstrated judgment, explain findings, recommend development activities, provide support and reassess progress. Advancement means moving toward the next development goal when the evidence supports it; further practice and support remain available throughout.'

/** Old capability pages now redirect to the closest pillar. */
export const legacyCapabilityRedirects: Record<string, string> = {
  '/reasoning-quality': pillarHref('evidence-and-reality-testing'),
  '/consequential-thinking': pillarHref('consequential-and-long-term-reasoning'),
  '/manipulation-resistance': pillarHref('manipulation-resistance'),
  '/emotional-regulation': pillarHref('emotional-regulation'),
  '/social-cooperation': pillarHref('responsible-agency-and-civic-reciprocity'),
  '/self-direction': pillarHref('responsible-agency-and-civic-reciprocity'),
  '/leadership-responsibility': pillarHref('responsible-agency-and-civic-reciprocity'),
}
